import _     from 'underscore';
import React from 'react';

import { Align, SemanticUIColor, Size, Uuid }                                                                                        from "@src/types";
import { ENTITY_ICONS }                                                                                                              from "@src/constants/ProductEnums";
import { MenuizedFocusContainer, MenuizedFocusContainerViewTypeConfig, MenuizedFocusedViewValue }                                    from "@src/views/containers/MenuizedFocusContainer";
import { BasicDialogContainer }                                                                                                      from "@src/views/components/modals/BasicDialogContainer";
import { BasicDialog }                                                                                                               from "@src/views/components/modals/BasicDialog";
import { BulkUpdateAttributeInfo }                                                                                                   from "@src/views/pages/projects/activities/bulk/BulkUpdateAttributeInfo";
import { StaticProjectActivityBulkUpdateNotes }                                                                                      from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateNotes";
import { StaticProjectActivityBulkUpdateMenuizedFocusFormContainer, StaticProjectActivityBulkUpdateMenuizedFocusFormContainerProps } from "@src/views/pages/projects/activities/bulk/StaticProjectActivityBulkUpdateMenuizedFocusFormContainer";
import { StaticProjectActivityBulkUpdateTiming }                                                                                     from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateTiming";
import { SupportedBulkUpdateFields, staticProjectActivityBulkUpdateFields }                                                          from "@src/views/pages/projects/operations/StaticProjectActivityBulkUpdateDialog";
import { StaticProjectActivityBulkUpdateHireType }                                                                                   from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateHireType";
import { StaticProjectActivityBulkUpdateOperationalCost }                                                                            from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOperationalCost";
import { StaticProjectActivityBulkUpdateOngoingCost }                                                                                from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateOngoingCost";
import { StaticProjectActivityBulkUpdateCriticality }                                                                                from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCriticality";
import { StaticProjectActivityBulkUpdateBusinessFunction }                                                                           from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateBusinessFunction";
import { StaticProjectActivityBulkUpdateCompletionState }                                                                            from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateCompletionState";
import { StaticProjectActivityBulkUpdateIsAtRisk }                                                                                   from "@src/views/pages/projects/activities/bulk/slices/StaticProjectActivityBulkUpdateIsAtRisk";


interface ProjectActivityBulkUpdateMultiDialogProps extends Pick<StaticProjectActivityBulkUpdateMenuizedFocusFormContainerProps, 'onUpdateComplete' | 'onUpdateError'> {
    title?: string,
    projectId?: null,
    projectVersionId?: null,
    projectActivityIds: Uuid[],
    showOptionsForTypes?: Array<SupportedBulkUpdateFields> | boolean,
}

const ProjectActivityBulkUpdateMultiDialogDefaultProps = {
    title:               'Bulk Update Activities',
    onUpdateComplete:    null,
    onUpdateError:       null,
    showOptionsForTypes: true,
} satisfies Partial<ProjectActivityBulkUpdateMultiDialogProps>

function StaticProjectActivityBulkUpdateMultiDialog( _props: ProjectActivityBulkUpdateMultiDialogProps ){

    const props = { ...ProjectActivityBulkUpdateMultiDialogDefaultProps, ..._props };

    const validUpdateTypes: SupportedBulkUpdateFields[] = _.keys( staticProjectActivityBulkUpdateFields ) as SupportedBulkUpdateFields[];

    const bulkUpdaterProps = {
        onUpdateComplete: props.onUpdateComplete,
        onUpdateError:    props.onUpdateError,
    }

    const showOptionsForType = ( type: SupportedBulkUpdateFields ) => {
        if( _.isArray( props.showOptionsForTypes ) ){
            return props.showOptionsForTypes.includes( type );
        }
        return props.showOptionsForTypes ?? true;
    }

    const getOptionProps = ( type: SupportedBulkUpdateFields ) => {
        if( !showOptionsForType( type ) ){
            return {
                maxOptionWidth: showOptionsForType( type ) ? null : '0px',
                renderOptions:  null
            }
        }

        return {};
    }

    const viewTypes = _.mapObject( staticProjectActivityBulkUpdateFields, ( v, k ) => k ) as Record<SupportedBulkUpdateFields, SupportedBulkUpdateFields>;

    return (
        <BasicDialog
            showSubmitButton={ false }
            controlAlign={ Align.CENTER }
            className={ 'no-padding' }
            cancelLabel={ 'Close' }>

            <BasicDialogContainer title={ props.title } titleColor={ SemanticUIColor.PURPLE } contentPadding={ Size.MD }>

                <MenuizedFocusContainer<SupportedBulkUpdateFields>
                    layoutProps={ {
                        minContainerHeight: '500px',
                    } }
                    initialViewType={ viewTypes.timing }
                    viewsTypes={ {

                        [ viewTypes.timing ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.timing,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.TIMING.NORMAL,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateTiming
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Timing' }
                                    description={ 'Updating the timing for activities will change when they start or end, and possibly update dependant activities.' }
                                />
                            ),
                            ...getOptionProps( viewTypes.timing )
                        },

                        [ viewTypes.isAtRisk ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.isAtRisk,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.RISK_RATING,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateIsAtRisk
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( viewTypes.criticality )
                        },

                        [ viewTypes.criticality ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.criticality,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.CRITICALITY_RATING,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateCriticality
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( viewTypes.criticality )
                        },

                        [ viewTypes.businessFunction ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.businessFunction,
                            menuIcon:      ENTITY_ICONS.BUSINESS_FUNCTIONS.ENTITY,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateBusinessFunction
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( viewTypes.businessFunction )
                        },

                        [ viewTypes.completionState ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.completionState,
                            menuIcon:      ENTITY_ICONS.ACTIVITIES.STATUS,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateCompletionState
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            ...getOptionProps( viewTypes.completionState )
                        },

                        [ viewTypes.hireType ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.hireType,
                            menuIcon:      ENTITY_ICONS.RESOURCING.HIRES.ENTITY,
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateHireType
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Hire Types' }
                                    description={ 'Updating the hire types for activities will transition them to a new resource, which may update your hiring budget calculations.' }
                                />
                            ),
                            ...getOptionProps( viewTypes.hireType )
                        },

                        [ viewTypes.operationalCost ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.operationalCost,
                            menuIcon:      ENTITY_ICONS.BUDGET.OPERATIONAL_COSTS,
                            exclude:       _.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateOperationalCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: null,
                            ...getOptionProps( viewTypes.operationalCost )
                        },

                        [ viewTypes.ongoingCost ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.ongoingCost,
                            menuIcon:      ENTITY_ICONS.BUDGET.ONGOING_COSTS,
                            exclude:       _.isEmpty( props.projectVersionId ),
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateOngoingCost
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: null,
                            ...getOptionProps( viewTypes.ongoingCost )
                        },

                        [ viewTypes.notes ]: {
                            menuLabel:     staticProjectActivityBulkUpdateFields.notes,
                            menuIcon:      'pencil',
                            renderContent: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <StaticProjectActivityBulkUpdateNotes
                                    supportModals={ false }
                                    projectId={ props.projectId }
                                    projectVersionId={ props.projectVersionId }
                                    projectActivityIds={ props.projectActivityIds }
                                    formContainer={ ( props ) => <StaticProjectActivityBulkUpdateMenuizedFocusFormContainer { ...{ ...props, ...bulkUpdaterProps } } /> }
                                />
                            ),
                            renderOptions: ( viewTypeInfo: MenuizedFocusedViewValue ) => (

                                <BulkUpdateAttributeInfo
                                    title={ 'About Notes' }
                                    description={ 'Updating the notes for activities will allow anyone reviewing information about the selected items to see additional details.' }
                                />
                            ),
                            ...getOptionProps( viewTypes.notes )
                        },

                    } as Record<SupportedBulkUpdateFields, MenuizedFocusContainerViewTypeConfig<SupportedBulkUpdateFields>> }
                />

            </BasicDialogContainer>

        </BasicDialog>
    );
}

export {
    StaticProjectActivityBulkUpdateMultiDialog,
    ProjectActivityBulkUpdateMultiDialogDefaultProps,
    type ProjectActivityBulkUpdateMultiDialogProps,
};
